import { EnvironmentInterface, FavoriteTypesEnum, HistoryTypesEnum } from '@campus/environment';
import { animations } from './animations';
import * as defaults from './environment.defaults';
import { icons } from './icons';
import { searchModes } from './search-modes';
import { svgs } from './svgs';
import { textMapping } from './text';

export const environment: Partial<EnvironmentInterface> = {
  production: true,
  enableDevTools: true,
  enableV2Routes: true,
  stage: 'staging',
  release: 'combell_with_subdomains.9a8c19783e8589344283b6fd316b06302aeb1381',
  platform: 'polpo',
  basePath: '/',
  crossOrigin: {
    trustedSuffix: '.staging.polpo.be',
  },
  website: {
    url: 'https://www.staging.polpo.be',
    title: 'POLPO - staging',
    favicon: 'assets/icons/favicons/favicon.ico',
    faqUrl: 'https://help.polpo.be',
    orderLicenseUrl: 'https://www.staging.polpo.be/bestel',
    activateLicenseUrl: 'https://www.staging.polpo.be/bestel/ik-heb-al-een-code',
    loadingUrl: 'https://leerkracht.staging.polpo.be/loading',
  },
  login: {
    url: 'https://leerkracht.staging.polpo.be/login',
    loginPresets: [
      { label: 'Student', username: 'student1', password: 'testje' },
      { label: 'Leerkracht', username: 'teacher1', password: 'testje' },
      { label: 'Beheerder (Sandrine)', username: 'schooladmin1', password: 'testje' },
    ],
  },
  profile: {
    url: '/settings/schools',
  },
  afterLogout: {
    url: 'https://leerkracht.staging.polpo.be/login',
    delay: 3000,
  },
  termPrivacy: {
    ...defaults.environment.termPrivacy,
  },
  iconMapping: icons,
  textMapping,
  svgMapping: svgs,
  api: {
    APIBase: 'https://api.staging.polpo.be',
    APIV2Base: 'https://api.staging.polpo.be/api-v2',
    APICampusBase: 'https://api.staging.polpo.be/api-campus',
    eventBroker: 'https://api.staging.polpo.be/event-broker',
  },
  features: {
    alerts: {
      enabled: false,
      hasAppBarDropDown: true,
      appBarPollingInterval: 3000,
    },
    messages: {
      enabled: false,
      hasAppBarDropDown: false,
    },
    errorManagement: {
      runMaintenanceCheckInInterceptor: true,
      managedStatusCodes: [500, 401, 404, 0],
      allowedErrors: [
        {
          status: 404,
          statusText: 'Not Found',
          urlRegex: 'http.*assets\\/icons.*.svg',
        },
        {
          status: 404,
          messageRegex: 'no_teacher_found_for_given_key',
        },
        {
          status: 401,
          urlRegex: 'http.*People\\/me.*',
        },
        {
          status: 401,
          urlRegex: 'http.*\\/EduContents/[0-9]+/requestURL',
        },
        {
          // Scoped palette is a theming enhancement; a failing call must never
          // block or surface an error to the user — the page still renders
          // without a custom palette.
          urlRegex: 'http.*\\/theming\\/scoped-palette.*',
        },
      ],
    },
    globalSearch: {
      enabled: true,
    },
    collectionManagement: {
      useFilter: false,
    },
    favorites: {
      allowedFavoriteTypes: [
        FavoriteTypesEnum.BOEKE,
        FavoriteTypesEnum.BUNDLE,
        FavoriteTypesEnum.EDUCONTENT,
        FavoriteTypesEnum.BOOK,
      ],
    },
    history: {
      allowedHistoryTypes: [
        HistoryTypesEnum.BOEKE,
        HistoryTypesEnum.EDUCONTENT,
        HistoryTypesEnum.AREA,
        HistoryTypesEnum.TASK,
        HistoryTypesEnum.BUNDLE,
        HistoryTypesEnum.HISTORY,
        HistoryTypesEnum.METHOD,
      ],
    },
    groups: { enabled: true },
    methods: {
      showEduNetNames: false,
    },
    news: {
      showNewsFeedToggle: false,
    },
    freshworks: {
      widgetId: 11000000600,
      productId: 11000000564,
      url: 'https://ink.staging.polpo.be/edu-contents-v2/{id}/general',
    },
    scorm: {
      cors: {
        enabled: true,
        allowedOrigin: 'https://api.staging.polpo.be',
      },
    },
  },
  sso: {
    facebook: {
      enabled: true,
      description: 'Facebook',
      logoIcon: 'facebook',
    },
    google: {
      enabled: true,
      description: 'Google',
      logoIcon: 'google',
    },
    smartschool: {
      enabled: true,
      description: 'Smartschool',
      logoIcon: 'smartschool',
      maxNumberAllowed: 10,
    },
    microsoft: {
      enabled: true,
      description: 'Microsoft',
      logoIcon: 'microsoft',
    },
    acm: {
      enabled: true,
      description: 'eID/Itsme',
      logoIcon: 'acm',
    },
    leerid: {
      enabled: true,
      provider: 'acm',
      description: 'LeerID',
      logoIcon: 'leerid',
    },
  },
  searchModes: searchModes,
  testing: {
    removeDataCyAttributes: false,
  },
  ui: {
    useInfoPanelStyle: true,
    miniCartTimeout: 3000,
    showFixedPriceSuffix: false,
    contact: {
      general: {
        mailAddress: 'polpo@diekeure.be',
      },
      order: { label: 'Mail de besteldienst', link: 'mailto:so@mailinator.be' },
      helpdesk: {
        label: 'polpo@diekeure.be',
        link: 'mailto:polpo@mailinator.be',
      },
      terms: {
        label: 'de verkoopsvoorwaarden',
        link: 'https://www.staging.polpo.be/legals/verkoopsvoorwaarden',
      },
    },
  },
  time: {
    msOffsetUntilUrgent: 1000 * 60 * 60 * 48,
  },
  assets: {
    basePath: 'https://leerkracht.staging.polpo.be/assets/',
    images: {
      methods: 'https://www.polpo.be/assets/images/methods',
      illustrations: 'https://leerkracht.staging.polpo.be/assets/images/illustrations',
      news: 'https://cdn.staging.polpo.be/avatar/',
      educontent: 'https://cdn.staging.polpo.be/avatar/',
      'image-dict': 'https://cdn.staging.polpo.be/avatar/',
    },
    rive: 'https://leerkracht.staging.polpo.be/assets/rive',
    animations,
  },
  results: {
    scoreMapping: [
      [70, 2],
      [50, 1],
      [0, 0],
    ],
    showStars: false,
  },
  googleAnalytics: {
    measurementId: '',
  },
  taskFeature: {
    showPaperAmount: false,
  },
  abTestConfig: {
    environment: 'staging',
    debugMode: false,
    anonymizeData: true,
    batchSize: 50,
    flushInterval: 5000,
    maxRetries: 3,
    enableGracefulDegradation: true,
    circuitBreakerThreshold: 10,
    autoTrackPageViews: true,
    scrollTrackingSelector: 'dcr-app-shell::main',
  },
};
